import { ListRange } from '@angular/cdk/collections';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { ROW_BORDER_BOTTOM_PX, UserTimelines } from '@wilson/interfaces';
import {
  DatesDemarcatorLinesComponent,
  RenderData,
  WeekendHolidayHighlighterComponent,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  DragAndDropModeService,
  TimelineInteractionService,
} from '@wilson/shift-timeline/services';
import {
  AssignedRegionVisibilityStateService,
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
} from '@wilson/shift-timeline/state';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
} from 'rxjs';
import { MoveAbsenceDropZoneComponent } from '../../components/move-absence-drop-zone/move-absence-drop-zone.component';
import { TimelineShiftTimeTagComponent } from '../../components/timeline-shift-time-tag/timeline-shift-time-tag.component';
import { UserTimelinesInfoCardComponent } from '../../components/user-timelines-info-card/user-timelines-info-card.component';
import { ToUserDataV2Pipe } from '../../pipes/to-user-data-v2.pipe';
import { MoveShiftDropZoneV2Component } from '../move-shift-drop-zone-v2/move-shift-drop-zone-v2.component';
import { TimelineUserRowShiftsV2Component } from '../timeline-user-row-shifts-v2/timeline-user-row-shifts-v2.component';

@Component({
  selector: 'wilson-timeline-user-row-v2',
  templateUrl: './timeline-user-row-v2.component.html',
  styleUrls: ['./timeline-user-row-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TimelineInteractionService],
  imports: [
    CommonModule,
    ScrollingModule,
    MoveShiftDropZoneV2Component,
    MoveAbsenceDropZoneComponent,
    TimelineShiftTimeTagComponent,
    UserTimelinesInfoCardComponent,
    TimelineUserRowShiftsV2Component,
    ToUserDataV2Pipe,
    DatesDemarcatorLinesComponent,
    WeekendHolidayHighlighterComponent,
  ],
})
export class TimelineUserRowV2Component implements OnInit {
  timelineInteractionService = inject(TimelineInteractionService);
  readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly store = inject(Store);
  protected assignedRegionVisibilityStateService = inject(
    AssignedRegionVisibilityStateService,
  );

  public resourcesColumnWidthPx$!: Observable<number>;
  public frameLength$!: Observable<number>;
  public ROW_BORDER_BOTTOM_PX = ROW_BORDER_BOTTOM_PX;
  public renderRadioInput = false;
  @Input()
  stickyHasShadow?: boolean;

  fullUserLength = 0;

  @Input() set users(value: UserTimelines[] | null) {
    if (value) {
      this.originalUsersSubject.next(value);
      this.fullUserLength = value.length;
    } else {
      this.originalUsersSubject.next([]);
    }
  }

  @Input()
  set theoreticalListRange(value: ListRange) {
    if (value) {
      this.listRangeSubject.next(value);
    }
  }

  @Input()
  rowHeightWithBorder = 0;

  @Input()
  weekendHolidayRenderData!: RenderData[];

  defaultRowHeight$!: Observable<number>;

  publicHolidaysRegion$ = this.store.select(AuthState.publicHolidaysRegion);
  visibleDaysRange$ = this.store.select(
    ShiftTimelineDataState.visibleDateRange,
  );

  protected timeframe$ = this.store.select(ShiftTimelineDataState.timeframe);

  private originalUsersSubject = new BehaviorSubject<UserTimelines[]>([]);
  private listRangeSubject = new BehaviorSubject<ListRange>({
    start: 0,
    end: 25,
  });
  protected trimmedUserList$ = combineLatest([
    this.listRangeSubject,
    this.originalUsersSubject,
  ]).pipe(
    debounceTime(10),
    map(([listRange, users]) => {
      return users.slice(0, listRange.end);
    }), // load all users starting from 0 to provide smooth scrolling experience
    distinctUntilChanged(),
  );

  ngOnInit(): void {
    this.resourcesColumnWidthPx$ = this.store.select(
      ShiftTimelineSettingsState.resourcesColumnWidthPx,
    );

    this.defaultRowHeight$ = this.store.select(
      ShiftTimelineSettingsState.defaultRowHeight,
    );

    this.frameLength$ = this.store.select(ShiftTimelineState.frameLength);
  }

  trackById(_: number, user: UserTimelines): string {
    return user.id as string;
  }
}
